import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, UserCheck, Calendar, Pill,
  ShoppingCart, CreditCard, X, Stethoscope, Settings as SettingsIcon,
  Clock, ChevronDown, ChevronRight, FlaskConical, TestTube, Bike,
  ArrowDownCircle, PiggyBank, LogOut, ClipboardList, Receipt, HeadphonesIcon,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS as P } from '../constants/permissions';

const mainNav = [
  { to: '/',                    icon: LayoutDashboard, label: 'Dashboard',         permissionId: P.DASHBOARD_VIEW   },
  { to: '/doctors',             icon: UserCheck,       label: 'Doctors',           permissionId: P.DOCTORS_VIEW     },
  { to: '/patients',            icon: Users,           label: 'Patients',          permissionId: P.PATIENTS_VIEW    },
  { to: '/appointments',        icon: Calendar,        label: 'Appointments',      permissionId: P.APPOINTMENTS_VIEW},
  { to: '/inventory',           icon: Pill,            label: 'Inventory',         permissionId: P.INVENTORY_VIEW   },
  { to: '/orders',              icon: ShoppingCart,    label: 'Orders',            permissionId: P.ORDERS_VIEW      },
  { to: '/payments',            icon: CreditCard,      label: 'Payments',          permissionId: P.PAYMENTS_VIEW    },
  { to: '/withdraw-requests',   icon: ArrowDownCircle, label: 'Withdraw Requests', permissionId: P.WITHDRAW_VIEW    },
  { to: '/deposit-requests',    icon: PiggyBank,       label: 'Deposit Requests',  permissionId: P.DEPOSIT_VIEW     },
  { to: '/support',             icon: HeadphonesIcon,  label: 'Support Tickets',   permissionId: P.SUPPORT_VIEW     },
  { to: '/settings',            icon: SettingsIcon,    label: 'Settings',          permissionId: P.SETTINGS_VIEW    },
  { to: '/policy',            icon: SettingsIcon,    label: 'Policies',          permissionId: P.SETTINGS_VIEW    },
];

const masterNav = [
  { to: '/master/specializations', icon: Stethoscope, label: 'Specializations', permissionId: P.MASTER_VIEW },
  { to: '/master/time-slots',      icon: Clock,       label: 'Time Slots',      permissionId: P.MASTER_VIEW },
];

const labNav = [
  { to: '/lab-tests',           icon: TestTube,      label: 'Lab Tests',       permissionId: P.LAB_TESTS_VIEW      },
  { to: '/lab-test-categories', icon: FlaskConical,  label: 'Test Categories', permissionId: P.LAB_CATEGORIES_VIEW },
  { to: '/lab-bookings',        icon: ClipboardList, label: 'Lab Bookings',    permissionId: P.LAB_BOOKINGS_VIEW   },
  { to: '/lab-payments',        icon: Receipt,       label: 'Lab Payments',    permissionId: P.LAB_PAYMENTS_VIEW   },
];

const deliveryNav = [
  { to: '/delivery-boys', icon: Bike, label: 'Delivery Boys', permissionId: P.DELIVERY_VIEW },
];

const NavItem = ({ to, icon: Icon, label, onClick, end }) => (
  <NavLink
    to={to}
    end={end}
    onClick={onClick}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
        isActive
          ? 'bg-medihub-primary text-white shadow-sm shadow-medihub-soft dark:shadow-medihub-dark'
          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
      }`
    }
  >
    <Icon className="w-5 h-5 shrink-0" />
    {label}
  </NavLink>
);

const ROLE_BADGE_COLORS = {
  Admin:    'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  Lab:      'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  Pharmacy: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  Support:  'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
};

const Sidebar = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { user, logout, hasPermission } = useAuth();
  const [masterOpen, setMasterOpen]   = useState(true);
  const [labOpen, setLabOpen]         = useState(true);
  const [deliveryOpen, setDeliveryOpen] = useState(true);

  const handleLogout = () => { logout(); navigate('/login'); };

  const visibleMain     = mainNav.filter(n => hasPermission(n.permissionId));
  const visibleMaster   = masterNav.filter(n => hasPermission(n.permissionId));
  const visibleLab      = labNav.filter(n => hasPermission(n.permissionId));
  const visibleDelivery = deliveryNav.filter(n => hasPermission(n.permissionId));

  return (
    <>
      {open && <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={onClose} />}

      <aside className={`fixed top-0 left-0 h-full w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 z-30 flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 lg:static lg:z-auto`}>
        {/* Logo */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center">
              <img src="/medihubLogo-removebg.png" alt="" />
            </div>
            <div>
              <span className="font-bold text-gray-900 dark:text-white text-lg">MediHub</span>
              <p className="text-xs text-gray-500 dark:text-gray-400 -mt-0.5">Admin Panel</p>
            </div>
          </div>
          <button onClick={onClose} className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* Main Menu */}
          {visibleMain.length > 0 && (
            <>
              <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 mb-2">Main Menu</p>
              {visibleMain.map(({ to, icon, label }) => (
                <NavItem key={to} to={to} icon={icon} label={label} onClick={onClose} end={to === '/'} />
              ))}
            </>
          )}

          {/* Master Management */}
          {visibleMaster.length > 0 && (
            <div className="pt-3">
              <button
                onClick={() => setMasterOpen(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <span className="flex items-center gap-2"><SettingsIcon className="w-3.5 h-3.5" />Master Management</span>
                {masterOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
              {masterOpen && (
                <div className="mt-1 space-y-1 pl-2">
                  {visibleMaster.map(({ to, icon, label }) => (
                    <NavItem key={to} to={to} icon={icon} label={label} onClick={onClose} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Lab Tests */}
          {visibleLab.length > 0 && (
            <div className="pt-2">
              <button
                onClick={() => setLabOpen(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <span className="flex items-center gap-2"><FlaskConical className="w-3.5 h-3.5" />Lab Tests</span>
                {labOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
              {labOpen && (
                <div className="mt-1 space-y-1 pl-2">
                  {visibleLab.map(({ to, icon, label }) => (
                    <NavItem key={to} to={to} icon={icon} label={label} onClick={onClose} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Delivery */}
          {visibleDelivery.length > 0 && (
            <div className="pt-2">
              <button
                onClick={() => setDeliveryOpen(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <span className="flex items-center gap-2"><Bike className="w-3.5 h-3.5" />Delivery</span>
                {deliveryOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
              {deliveryOpen && (
                <div className="mt-1 space-y-1 pl-2">
                  {visibleDelivery.map(({ to, icon, label }) => (
                    <NavItem key={to} to={to} icon={icon} label={label} onClick={onClose} />
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 shrink-0 space-y-2">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 bg-medihub-light dark:bg-medihub-dark rounded-full flex items-center justify-center text-medihub-secondary dark:text-medihub-highlight font-semibold text-sm shrink-0">
              {user?.name?.[0] || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user?.name || 'Admin User'}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <ShieldCheck className="w-3 h-3 text-gray-400 shrink-0" />
                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${ROLE_BADGE_COLORS[user?.roleLabel] || 'bg-gray-100 text-gray-600'}`}>
                  {user?.roleLabel || 'Admin'}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />Sign Out
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
